// Typisierte Aufrufe der Kern-API /api/core/* (Besitzer: api-kern) und Query-Keys.
import type {
  ApiToken,
  AuthConfig,
  BrowserAccount,
  InviteInfo,
  LoginEvent,
  Me,
  Notification,
  Page,
  Persona,
  SearchGroup,
  SessionInfo,
  Setting,
  Task,
  TaskCategory,
  TaskCounts,
} from '@bar/contracts/core';
import { api } from './client';

const BASE = '/api/core';

/** Query-Keys des Kerns. Module bauen ihre eigenen unter ['<modul>', …]. */
export const coreKeys = {
  all: ['core'] as const,
  authConfig: ['core', 'auth-config'] as const,
  me: ['core', 'me'] as const,
  personas: ['core', 'personas'] as const,
  browserAccounts: ['core', 'browser-accounts'] as const,
  sessions: ['core', 'sessions'] as const,
  logins: ['core', 'logins'] as const,
  tokens: (scope: 'own' | 'all' = 'own') => ['core', 'tokens', scope] as const,
  tasks: (params: TaskListParams = {}) => ['core', 'tasks', params] as const,
  tasksAll: ['core', 'tasks'] as const,
  taskCounts: ['core', 'task-counts'] as const,
  notifications: (params: { unread?: boolean } = {}) => ['core', 'notifications', params] as const,
  notificationsAll: ['core', 'notifications'] as const,
  unreadCount: ['core', 'notifications-unread'] as const,
  search: (q: string) => ['core', 'search', q] as const,
  settings: (prefix?: string) => ['core', 'settings', prefix ?? ''] as const,
};

export interface TaskListParams {
  status?: 'open' | 'done' | 'all';
  category?: TaskCategory;
  includeSnoozed?: boolean;
  limit?: number;
  cursor?: string;
}

type Items<T> = { items: T[] };

export const coreApi = {
  // Anmeldung
  authConfig: () => api.get<AuthConfig>(`${BASE}/auth/config`),
  me: () => api.get<Me>(`${BASE}/me`),
  updateMe: (
    patch: Partial<Pick<Me['account'], 'displayName' | 'theme'>> & {
      notificationPrefs?: Record<string, { inApp: boolean; email: boolean }>;
    },
  ) => api.patch<Me>(`${BASE}/me`, patch),
  login: (email: string, password: string) =>
    api.post<Me>(`${BASE}/auth/login`, { email, password }),
  loginPersona: (key: string) => api.post<Me>(`${BASE}/auth/persona`, { key }),
  logout: (all = false) => api.post<{ ok: true }>(`${BASE}/auth/logout`, { all }),
  browserAccounts: () =>
    api.get<Items<BrowserAccount>>(`${BASE}/auth/accounts`).then((r) => r.items),
  switchAccount: (accountId: string) => api.post<Me>(`${BASE}/auth/switch`, { accountId }),
  personas: () => api.get<Items<Persona>>(`${BASE}/personas`).then((r) => r.items),
  invite: (body: { email: string; displayName?: string; employeeId?: string; roles?: string[] }) =>
    api.post<{ accountId: string; status: 'invited' }>(`${BASE}/auth/invite`, body),
  inviteInfo: (token: string) =>
    api.get<InviteInfo>(`${BASE}/auth/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string) =>
    api.post<Me>(`${BASE}/auth/invite/accept`, { token, password }),
  requestReset: (email: string) =>
    api.post<{ ok: true; message: string }>(`${BASE}/auth/reset`, { email }),
  confirmReset: (token: string, password: string) =>
    api.post<Me>(`${BASE}/auth/reset/confirm`, { token, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<{ ok: true }>(`${BASE}/auth/password`, { currentPassword, newPassword }),

  // Sitzungen und Tokens
  sessions: () => api.get<Items<SessionInfo>>(`${BASE}/sessions`).then((r) => r.items),
  revokeSession: (id: string) => api.delete(`${BASE}/sessions/${id}`),
  revokeOtherSessions: () => api.post<{ revoked: number }>(`${BASE}/sessions/revoke-others`),
  logins: () => api.get<Items<LoginEvent>>(`${BASE}/sessions/logins`).then((r) => r.items),
  tokens: (scope: 'own' | 'all' = 'own') =>
    api
      .get<Items<ApiToken>>(`${BASE}/tokens`, {
        query: { scope: scope === 'all' ? 'all' : undefined },
      })
      .then((r) => r.items),
  createToken: (body: { name: string; scope: 'read' | 'write'; expiresInDays?: 30 | 90 | 365 }) =>
    api.post<{ token: ApiToken; secret: string }>(`${BASE}/tokens`, body),
  revokeToken: (id: string) => api.delete(`${BASE}/tokens/${id}`),
  deviceCode: (userCode: string) =>
    api.get<{
      userCode: string;
      client: string;
      deviceName: string;
      scopes: string[];
      status: string;
      expiresAt: string;
    }>(`${BASE}/device/codes/${encodeURIComponent(userCode)}`),
  decideDevice: (userCode: string, decision: 'approve' | 'deny') =>
    api.post<{ status: string }>(`${BASE}/device/codes/${encodeURIComponent(userCode)}/decision`, {
      decision,
    }),

  // Inbox
  tasks: (params: TaskListParams = {}) =>
    api.get<Page<Task>>(`${BASE}/tasks`, { query: { ...params } }),
  taskCounts: () => api.get<TaskCounts>(`${BASE}/tasks/counts`),
  completeTask: (id: string) => api.post<Task>(`${BASE}/tasks/${id}/complete`),
  snoozeTask: (id: string, days: 1 | 3 | 7) =>
    api.post<Task>(`${BASE}/tasks/${id}/snooze`, { days }),
  notifications: (params: { unread?: boolean; limit?: number; cursor?: string } = {}) =>
    api.get<Page<Notification>>(`${BASE}/notifications`, { query: { ...params } }),
  unreadCount: () =>
    api.get<{ count: number }>(`${BASE}/notifications/unread-count`).then((r) => r.count),
  markNotificationRead: (id: string) =>
    api.post<{ updated: number }>(`${BASE}/notifications/${id}/read`),
  markAllNotificationsRead: () => api.post<{ updated: number }>(`${BASE}/notifications/read-all`),

  // Suche, Einstellungen, Dateien
  search: (q: string, limit?: number, signal?: AbortSignal) =>
    api
      .get<{ groups: SearchGroup[] }>(`${BASE}/search`, { query: { q, limit }, signal })
      .then((r) => r.groups),
  settings: (prefix?: string) =>
    api.get<Items<Setting>>(`${BASE}/settings`, { query: { prefix } }).then((r) => r.items),
  putSetting: (key: string, value: unknown) =>
    api.put<Setting>(`${BASE}/settings/${encodeURIComponent(key)}`, { value }),
  fileUrl: (id: string) => `${BASE}/files/${id}`,
  signedFileUrl: (id: string) =>
    api.get<{ url: string; expiresAt: string }>(`${BASE}/files/${id}/url`),
};
